import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import useSession from '@/hooks/useSession';
import styles from './root-layout.module.scss';
import { classnames } from '@/utils/functions';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Slide, toast, ToastContainer } from 'react-toastify';

const RootLayout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { session, setSession, refreshSession } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Track in-flight refresh to prevent concurrent calls while allowing retries after completion
  const refreshInFlight = useRef<Promise<void> | null>(null);
  const publicURLs = useRef<string[]>(['/sign-in', '/auth/sso/callback']);

  useEffect(() => {
    if (pathname === '/sign-in') {
      setIsLoading(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (publicURLs.current.includes(pathname)) {
      setIsLoading(false);
      return;
    } else if (import.meta.env.VITE_MAINTENANCE_END) {
      navigate('/status', { replace: true });
      return;
    } else if (session && session.email) {
      setIsLoading(false);
      return;
    } else {
      const checkIfLoggedIn = async () => {
        // If a refresh is already in progress, wait for it instead of starting another
        if (refreshInFlight.current) {
          try {
            await refreshInFlight.current;
          } catch {
            // Refresh failed, but we already handled it
          }
          return;
        }

        // Start a new refresh and track the promise
        refreshInFlight.current = refreshSession();

        try {
          await refreshInFlight.current;
          setIsLoading(false);
        } catch (err) {
          console.error(err);
          setIsLoading(false);
        } finally {
          // Clear the in-flight promise to allow future retries
          refreshInFlight.current = null;
        }
      };
      checkIfLoggedIn();
    }
  }, [session, navigate, pathname, setSession, refreshSession]);

  useEffect(() => {
    // Don't dismiss toasts on public URLs (preserves SSO error messages)
    if (!publicURLs.current.includes(pathname)) {
      toast.dismiss();
    }
  }, [pathname]);

  return (
    <main
      className={classnames(styles.root, { [styles['loading']]: isLoading })}
    >
      {isLoading ? (
        <div className={styles['loading-wrapper']}>
          <img
            className={styles['loading-logo']}
            src="/gannet-green.png"
            height="150"
            alt="Gannet green logo"
          />
          <LoadingSpinner />
        </div>
      ) : (
        <Outlet />
      )}
      <ToastContainer
        className={styles['toasty']}
        position="bottom-right"
        pauseOnHover={true}
        autoClose={4000}
        transition={Slide}
      />
    </main>
  );
};

export default RootLayout;
